import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
} from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";

import { CpnwContainer } from "hooks/useCpnw";

import {
  AcademicInvoicingForm,
  InputBox,
  InstitutionForm,
  Loading,
  PackageList,
  RadioCard,
  RadioSelectButton,
  SubmitButton,
  TextArea,
} from "@cpnw/ui";

import { ProgramInfoForm } from "../../../../components/ProgramInfoForm";
import { RepresentativeForm } from "../../../../components/RepresentativeForm";

import styles from "./NewAcademicForm.module.css";

// -------------------- InstitutionSection --------------------
const InstitutionSection = forwardRef((props, ref) => {
  const { data, onChange } = props;

  // Reference to scroll to this section on validation error
  const institutionSectionRef = useRef();

  // Ref for calling validation inside InstitutionForm
  const institutionFormRef = useRef();

  // Expose validate method to parent component
  useImperativeHandle(ref, () => ({
    validate: () => {
      const institutionResult = institutionFormRef.current?.validate?.();

      // Scroll into view if validation fails
      requestAnimationFrame(() => {
        if (!institutionResult && institutionSectionRef.current) {
          institutionSectionRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });

      return institutionResult;
    }
  }));

  return (
    <div className={styles.sectionContainer} ref={institutionSectionRef}>
      <h3 className={styles.subHeader}>Higher Education Institution</h3>
      <p>
        {/* Description of the institution section */}
        Please complete the required information for your educational
        institution below. This information will be used for official
        correspondence from CPNW. The primary contact should be the individual
        responsible for oversight of the program accounts you are requesting.
      </p>
      <InstitutionForm
        ref={institutionFormRef}
        institutionFormData={data}
        placeholders={{
          name: "Parent Educational Name",
          address: "Educational Mailing Address",
        }}
        // Update parent state when form changes
        onChange={(updated) => {
          onChange({ ...data, ...updated })
        }}
      />
    </div>
  )
});


// -------------------- ProgramSection --------------------
const ProgramSection = forwardRef((props, ref) => {
  const { data, onChange, onRemove } = props;

  const programSectionRef = useRef();
  const programInfoFormRef = useRef();

  const disciplineSectionRef = useRef();
  const disciplineFormRef = useRef();

  const partnerSectionRef = useRef();
  const partnerFormRef = useRef();

  const annualRatesSectionRef = useRef();
  const annualRatesFormRef = useRef();

  const payPerServiceSectionRef = useRef();
  const payPerServiceFormRef = useRef();

  const multiYearSectionRef = useRef();
  const multiYearFormRef = useRef();

  const paymentMethodSectionRef = useRef();
  const paymentMethodFormRef = useRef();

  const invoicingSectionRef = useRef();
  const invoicingFormRef = useRef();

  const placementSectionRef = useRef();
  const placementFormRef = useRef();

  const [isResetCheckrPaymentOptions, setResetCheckrPaymentOptions] = useState(false);
  const [isResetWatchPaymentOptions, setResetWatchPaymentOptions] = useState(false);

  const [isResetMultiYearPackages, setResetMultiYearPackages] = useState(false);

  const [isResetRepresentativeForm, setResetRepresentativeForm] = useState(false);

  const [disciplineError, setDisciplineError] = useState("");
  const [annualRatesError, setAnnualRatesError] = useState("");
  const [placementsError, setPlacementsError] = useState("");

  // Helper to avoid crashes if an array is missing
  const asList = (v) => (Array.isArray(v) ? v : []);

  const isSelectedCheckrBackgroundCheck = (checkrBackgroundPackages) => {
    return checkrBackgroundPackages?.find((m) =>
      m.name.startsWith("CPNW: Checkr Background Check Service")
    )?.isSelected || false;
  };

  // Batch updates in one call
  const applyUpdate = useCallback(
    (patch) => {
      const newData = { ...data, ...patch };

      if (isSelectedCheckrBackgroundCheck(newData.checkrBackgroundPackages)) {
        const studentSelfPay = newData.paymentMethodPackages?.find((m) =>
          m.name.startsWith("Student/Self Pay")
        );

        if (studentSelfPay) {
          studentSelfPay.isSelected = false;
        }
      }
      onChange?.(newData);
    },
    [data, onChange]
  );

  // Helper flags
  const isMultiYearSelected = data.multiYearPackages.some((p) => p.isSelected);

  useImperativeHandle(ref, () => ({
    validate: () => {
      const programInfoFormResult = programInfoFormRef.current?.validate?.();

      const disciplineFormResult = data.programDisciplinePackages.some(pkg => pkg.isSelected);

      setDisciplineError(disciplineFormResult ? "" : "Please select a package");

      const annualRatesFormResult = data.annualRatesPackages.some(pkg => pkg.isSelected) || data.multiYearPackages.some(pkg => pkg.isSelected);
      setAnnualRatesError(annualRatesFormResult ? "" : "Please select a package");

      const multiYearFormResult = multiYearFormRef.current?.validate?.();

      const invoicingFormResult = invoicingFormRef.current?.validate?.();

      const placementsFormResult = data.placements.length > 0;
      setPlacementsError(placementsFormResult ? "" : "At least one placement is required.");

      requestAnimationFrame(() => {
        if (programInfoFormResult === false && programSectionRef.current) {
          programSectionRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (disciplineFormResult === false && disciplineSectionRef.current) {
          disciplineSectionRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (annualRatesFormResult === false && annualRatesSectionRef.current) {
          annualRatesSectionRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (multiYearFormResult === false && multiYearSectionRef.current) {
          multiYearSectionRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (invoicingFormResult === false && invoicingSectionRef.current) {
          invoicingSectionRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (placementsFormResult === false && placementSectionRef.current) {
          placementSectionRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });

      return programInfoFormResult & disciplineFormResult && annualRatesFormResult && invoicingFormResult && placementsFormResult;
    }
  }));

  const renderPaymentMethodPackages = () => {
    const methodSelfPay = "Student/Self Pay";
    const packageNotSelfpay = data.paymentMethodPackages.filter(
      (m) => !m.name.startsWith(methodSelfPay)
    );
    const packageSelfpay = data.paymentMethodPackages.filter((m) =>
      m.name.startsWith(methodSelfPay)
    );

    const handleSelect = (selectedItem) => {
      const updated = data.paymentMethodPackages.map((m) => ({
        ...m,
        isSelected: selectedItem ? m.id === selectedItem.id : false,
      }));

      applyUpdate({ paymentMethodPackages: updated });
    };

    return (
      <>
        <PackageList
          allowDeselect
          packages={packageNotSelfpay}
          onSelect={(selectedItem) => handleSelect(selectedItem)}
        />
        <PackageList
          allowDeselect
          packages={packageSelfpay}
          onSelect={(selectedItem) => handleSelect(selectedItem)}
          isDisabled={isSelectedCheckrBackgroundCheck(
            data?.checkrBackgroundPackages
          )}
        />
        {data.checkrBackgroundPackages.some((item) => item.isSelected) && data.checkrPaymentMethods[0] && (
          <RadioCard
            name={data.checkrPaymentMethods[0].name}
            description={data.checkrPaymentMethods[0].description}
            options={data.checkrPaymentMethods[0].options.filter(item => item.label != methodSelfPay).map(item => item.label)}
            selected={
              data.checkrPaymentMethods[0].options.find(opt => opt.isSelected)?.label ??
              data.checkrPaymentMethods[0].options.find(opt => opt.isDefault)?.label
            }
            onChange={(selectedLabel) => {
              const updated = data.checkrPaymentMethods.map((method) => ({
                ...method,
                options: method.options.map((opt) => ({
                  ...opt,
                  isSelected: opt.label === selectedLabel,
                })),
              }));

              applyUpdate({ checkrPaymentMethods: updated });
            }}
          />
        )}
        {data.watchReportPackages.some((item) => item.isSelected) && data.watchPaymentMethods[0] && (
          <RadioCard
            name={data.watchPaymentMethods[0].name}
            description={data.watchPaymentMethods[0].description}
            options={data.watchPaymentMethods[0].options.map(item => item.label)}
            selected={
              data.watchPaymentMethods[0].options.find(opt => opt.isSelected)?.label ??
              data.watchPaymentMethods[0].options.find(opt => opt.isDefault)?.label
            }
            onChange={(selectedLabel) => {
              const updated = data.watchPaymentMethods.map((method) => ({
                ...method,
                options: method.options.map((opt) => ({
                  ...opt,
                  isSelected: opt.label === selectedLabel,
                })),
              }));

              applyUpdate({ watchPaymentMethods: updated });
            }}
          />
        )}
      </>
    );
  };

  return (
    <div>
      <div className={styles.sectionContainer} ref={programSectionRef}>
        <div className={styles.headerRow}>
          <h3 className={styles.subHeader}>Program Information</h3>
          {onRemove && (
            <div className={styles.removeButtonContainer}>
              <button
                type="button"
                className={styles.removeButton}
                onClick={onRemove}>
                <FontAwesomeIcon icon={faTrash} className={styles.trashIcon} /> Remove
              </button>
            </div>
          )}
        </div>
        <p>
          CPNW accounts are organized according to degree/program. You must
          request an account for each degree/program. Click the{" "}
          <span className={styles.addProgram}>+Add Program</span> link at the
          bottom of the form to add additional account requests. You must
          identify an account coordinator and a backup coordinator for each
          program. Coordinators may manage more than one program. If you don't
          currently have a backup coordinator, you must specify your primary
          coordinator as the backup.
        </p>
        <ProgramInfoForm
          ref={programInfoFormRef}
          programInfoFormData={data.programInfo}
          onChange={(updated) => {
            applyUpdate({ programInfo: updated });
          }}
        />
      </div>
      <div className={styles.sectionContainer} ref={disciplineSectionRef}>
        <h3 className={styles.subHeader}>Program Discipline</h3>
        <p>
          To ensure accurate identification within the CPNW system, please{" "}
          <span className={styles.bold}>
            select the program discipline from the dropdown menu that best
            represents your program
          </span>{" "}
          and aligns with the clinical experience expected for your students.
          This section is crucial, as it determines the site-specific onboarding
          requirements assigned by our healthcare partners. Please choose the
          discipline that most accurately reflects the anticipated clinical
          experience for your students.
        </p>
        <p className={styles.requiredMessage}>**Required</p>
        {disciplineError && (
          <p className={styles.errorMessage}>{disciplineError}</p>
        )}
        <PackageList
          allowDeselect
          // ref={annualRatesFormRef}
          packages={data.programDisciplinePackages}
          onSelect={(selectedItem) => {
            const updated = data.programDisciplinePackages.map((pkg) => ({
              ...pkg,
              isSelected: selectedItem ? pkg.id === selectedItem.id : false,
            }));
            setDisciplineError("")

            var isUpdated = false;

            for(const pkg of updated){
              if(pkg.isSelected){
                if(pkg.name.includes("Allied")){
                  //https://www.wrike.com/open.htm?id=4314323751
                  // Automatically set invoicing frequency to Quarterly for Allied Health disciplines
                  const invoicingData = data.invoicingInfo;
                  invoicingData.frequency = "Quarterly";
                  applyUpdate({ programDisciplinePackages: updated, invoicingInfo: invoicingData });
                  isUpdated = true;
                }
              }
            }
            
            if(!isUpdated) {
              applyUpdate({ programDisciplinePackages: updated });
            }
          }}
          isDisabled={false}
        />
      </div>
      <div className={styles.sectionContainer} ref={partnerSectionRef}>
        <h3 className={styles.subHeader}>CPNW Member vs Community Partner</h3>
        <p>
          Certain CPNW Education Packages are exclusively available to CPNW
          Members. Multi-year Education Packages, in particular, are accessible
          only to programs with CPNW Membership. Your program's designation -
          either CPNW Member or Community Partner - is specified in your CPNW
          Service Agreement document. Please select the appropriate designation
          for your program.
        </p>
        <div className={styles.radioContainer}>
          <div className={styles.items}>
            <RadioSelectButton
              isSelected={data.partnerType === PartnerTypes.CPNW_MEMBER}
              onChange={(updated) => {

                // Clear any multi-year selection when annual is chosen
                const clearedMultiYear = data.multiYearPackages.map((pkg) => ({
                  ...pkg,
                  isSelected: false,
                }));

                const clearedRepresentativeInfo = asList(data.representativeInfo).map((p) => ({
                  name: "",
                  email: "",
                }));

                applyUpdate({ partnerType: updated, multiYearPackages: clearedMultiYear, representativeInfo: clearedRepresentativeInfo });

              }}
              value={PartnerTypes.CPNW_MEMBER}
              text="CPNW Member"
            />
            <RadioSelectButton
              isSelected={data.partnerType === PartnerTypes.COMMUNITY_PARTNER}
              onChange={(updated) => {
                // Clear any multi-year selection when annual is chosen
                const clearedMultiYear = data.multiYearPackages.map((pkg) => ({
                  ...pkg,
                  isSelected: false,
                }));

                const clearedRepresentativeInfo = asList(data.representativeInfo).map((p) => ({
                  name: "",
                  email: "",
                }));

                applyUpdate({ partnerType: updated, multiYearPackages: clearedMultiYear, representativeInfo: clearedRepresentativeInfo });

              }}
              value={PartnerTypes.COMMUNITY_PARTNER}
              text="Community Partner"
            />
          </div>
        </div>
      </div>

      {
        /** 
         * Section SELECT SERVICE PACKAGE - component for selecting an Annual Rates Service Package.
         *
         * Behavior:
         * - Allows deselection of the selected annual rates package (`allowDeselect`).
         * - Not disabled (`isDisabled={false}`).
         *
         * onSelect Logic:
         * - Updates `annualRatesPackages` to mark the selected package (or none if deselected).
         * - Clears any selected multi-year package to prevent conflicting selections.
         * - Clears `representativeInfo` as multi-year-specific info should not persist when switching.
         *
         * State updates:
         * - Applies all changes in a single `applyUpdate` call:
         *   - `annualRatesPackages`: updated with the selected item.
         *   - `multiYearPackages`: all deselected.
         *   - `representativeInfo`: reset to empty fields.
         */
      }
      <div className={styles.sectionContainer} ref={annualRatesSectionRef}>
        <h3 className={styles.subHeader}>Select Service Package</h3>
        <p>
          CPNW offers annual and multi-year packages designed to meet the needs
          of education partners.
        </p>
        <p className={styles.requiredMessage}>**Required</p>
        {annualRatesError && (
          <p className={styles.errorMessage}>{annualRatesError}</p>
        )}
        <PackageList
          allowDeselect
          // ref={disciplineFormRef}
          packages={data.annualRatesPackages}
          onSelect={(selectedItem) => {
            const updated = data.annualRatesPackages.map((pkg) => ({
              ...pkg,
              isSelected: selectedItem ? pkg.id === selectedItem.id : false,
            }));

            // clear any multi-year selection when annual is chosen
            const clearedMultiYear = data.multiYearPackages.map((pkg) => ({
              ...pkg,
              isSelected: false,
            }));
            const clearedRepresentativeInfo = asList(data.representativeInfo).map((p) => ({
              name: "",
              email: "",
            }));
            setAnnualRatesError("")

            applyUpdate({ annualRatesPackages: updated, multiYearPackages: clearedMultiYear, representativeInfo: clearedRepresentativeInfo });
          }}
          isDisabled={false}
        />
      </div>

      {
        /** 
         * Section MULTI-YEAR PACKAGES - component for selecting a Multi-Year Service Package.
         *
         * Behavior:
         * - Allows deselection of the selected package (`allowDeselect`).
         * - Disabled when the partner type is COMMUNITY_PARTNER.
         * 
         * onSelect Logic:
         * - Updates the `multiYearPackages` list to mark the selected package (or none if deselected).
         * - Clears `representativeInfo` regardless of selection.
         * 
         * If the user DESELECTS a multi-year package:
         *   - Only `multiYearPackages` and `representativeInfo` are updated.
         * 
         * If the user SELECTS a multi-year package:
         *   - Other package categories (`watchReportPackages`, `checkrBackgroundPackages`, and `annualRatesPackages`)
         *     are cleared (deselected) to avoid conflicting selections.
         *   - All changes are applied in a single batched state update via `applyUpdate`.
         */
      }
      <div className={styles.sectionContainer} ref={multiYearSectionRef}>
        <h3 className={styles.subHeader}>Multi-year Packages</h3>
        {data.partnerType === PartnerTypes.COMMUNITY_PARTNER && (
          <span className={styles.memberOnly}>
            *Only available to CPNW Members.
          </span>
        )}
        <PackageList
          allowDeselect
          // ref={multiYearFormRef}
          packages={data.multiYearPackages}
          onSelect={(selectedItem) => {
            // update multi-year selection
            const updated = asList(data.multiYearPackages).map((pkg) => ({
              ...pkg,
              isSelected: selectedItem ? pkg.id === selectedItem.id : false,
            }));
            const clearedRepresentativeInfo = asList(data.representativeInfo).map((p) => ({
              name: "",
              email: "",
            }));

            // if de-selecting multi-year: only update multiYearPackages (+ any side effects)
            if (!selectedItem) {
              applyUpdate({ multiYearPackages: updated, representativeInfo: clearedRepresentativeInfo });
              return;
            }

            // if selecting multi-year: clear related lists in the same update
            const clearedWatch = asList(data.watchReportPackages).map((p) => ({
              ...p,
              isSelected: false,
            }));
            const clearedCheckr = asList(data.checkrBackgroundPackages).map((p) => ({
              ...p,
              isSelected: false,
            }));
            const clearedAnnualRates = asList(data.annualRatesPackages).map((p) => ({
              ...p,
              isSelected: false,
            }));
            setAnnualRatesError("");

            // single, batched state update
            applyUpdate({
              multiYearPackages: updated,
              watchReportPackages: clearedWatch,
              checkrBackgroundPackages: clearedCheckr,
              annualRatesPackages: clearedAnnualRates,
              representativeInfo: clearedRepresentativeInfo,
            });
          }}
          isDisabled={data.partnerType === PartnerTypes.COMMUNITY_PARTNER}
        />

        {data.partnerType !== PartnerTypes.COMMUNITY_PARTNER &&
          data.multiYearPackages.some((item) => item.isSelected) && (
            <>
              <p>
                <span className={styles.bold}>
                  To establish system access to vendors supported by this
                  package please provide the following:
                </span>
              </p>
              <RepresentativeForm
                ref={multiYearFormRef}
                representativeFormData={data.representativeInfo}
                onChange={(updated) => {
                  applyUpdate({ representativeInfo: updated });
                }}
              />
            </>
          )}
      </div>

      <div className={styles.sectionContainer} ref={payPerServiceSectionRef}>
        <h3 className={styles.subHeader}>Pay Per Service Options</h3>
        <p>
          Some programs may identify the need for additional services beyond
          their selected package.{" "}
          <span className={styles.bold}>Add-On Services</span> provide the
          fexibility to customize your package by incorporating{" "}
          <span className={styles.bold}>National Background Checks</span> and/or{" "}
          <span className={styles.bold}>WATCH Reports</span> if they are not
          already included. If either of these services is required for your
          program, but not part of your initial package selection, you have the
          option to <span className={styles.bold}>add them separately</span> to
          ensure compliance and streamline your onboarding process.
        </p>

        <PackageList
          allowDeselect
          // ref={payPerServiceFormRef}
          packages={data.checkrBackgroundPackages}
          onSelect={(selectedItem) => {
            const updated = data.checkrBackgroundPackages.map((pkg) => ({
              ...pkg,
              isSelected: selectedItem ? pkg.id === selectedItem.id : false,
            }));
            applyUpdate({ checkrBackgroundPackages: updated });


            if (!selectedItem) {
              // Checkr logic
              const hasSelectedCheckr = data.checkrBackgroundPackages.some(item => item.isSelected);
              const hasCheckrPayment = data.checkrPaymentMethods.length > 0;

              if (!hasSelectedCheckr || !hasCheckrPayment) {
                const clearedCheckrMethods = data.checkrPaymentMethods.map(method => ({
                  ...method,
                  options: method.options.map(opt => ({
                    ...opt,
                    isSelected: opt.isDefault === true,
                  })),
                }));

                applyUpdate({ checkrPaymentMethods: clearedCheckrMethods });
              }
            }
          }}
          isDisabled={isMultiYearSelected}
        />

        <PackageList
          allowDeselect
          packages={data.watchReportPackages}
          onSelect={(selectedItem) => {
            const updated = data.watchReportPackages.map((pkg) => ({
              ...pkg,
              isSelected: selectedItem ? pkg.id === selectedItem.id : false,
            }));
            applyUpdate({ watchReportPackages: updated });

            if (!selectedItem) {
              // WATCH logic
              const hasSelectedWatch = data.watchReportPackages.some(item => item.isSelected);
              const hasWatchPayment = data.watchPaymentMethods.length > 0;

              if (!hasSelectedWatch || !hasWatchPayment) {
                const resetWatchMethods = data.watchPaymentMethods.map(method => ({
                  ...method,
                  options: method.options.map(opt => ({
                    ...opt,
                    isSelected: opt.isDefault === true,
                  })),
                }));

                applyUpdate({ watchPaymentMethods: resetWatchMethods });
              }
            }
          }}
          isDisabled={isMultiYearSelected}
        />
      </div>

      <div className={styles.sectionContainer} ref={paymentMethodSectionRef}>
        <h3 className={styles.subHeader}>
          Payment Method for Package Selection
        </h3>
        {renderPaymentMethodPackages()}
      </div>

      <div className={styles.sectionContainer} ref={invoicingSectionRef}>
        <h3 className={styles.subHeader}>Invoicing Information</h3>
        <p>Invoicing will occur during the month(s) selected:</p>
        <p>Allied Health program disciplines can only be billed quarterly. For any questions, please contact <a href="mailto:support@cpnw.org">support@cpnw.org</a></p>
        <AcademicInvoicingForm
          ref={invoicingFormRef}
          academicInvoicingData={data.invoicingInfo}
          //https://www.wrike.com/open.htm?id=4314323751
          //Disable invoicing frequency change if Allied Health discipline is selected
          disabled={data.programDisciplinePackages.find(pkg => pkg.isSelected && pkg.name.includes("Allied")) != null}
          onChange={(updated) => {

            applyUpdate({ invoicingInfo: updated });
          }}
        />
      </div>

      <div className={styles.sectionContainer} ref={placementSectionRef}>
        <p>
          Please list the CPNW member clinical sites where your students have{" "}
          <span className={styles.italics}>
            confirmed placements in the current academic year
          </span>
        </p>
        <p>
          <span className={styles.requiredMessage}>**Required</span>
        </p>
        {placementsError && <p className={styles.errorMessage}>{placementsError}</p>}
        <TextArea
          textAreaClassName={styles.textAreaRound}
          value={data.placements}
          setValue={(e) => {

            applyUpdate({ placements: e.target.value });
          }
          }
        />

        <p>Please use the text area below for any additional information or questions:</p>

        <InputBox
          className={styles.inputBoxRound}
          value={data.questions}
          setValue={(e) => {

            applyUpdate({ questions: e.target.value });
          }}
        />
      </div>
    </div>
  )
});

// -------------------- MainProgram --------------------
const MainProgram = forwardRef((props, ref) => {
  const { data, onChange } = props;

  // Refs for scrolling or future validation handling
  const institutionSectionRef = useRef();
  const institutionRef = useRef();

  const programSectionRef = useRef();
  const programRef = useRef();

  // Batch updates in one call
  const applyUpdate = useCallback(
    (patch) => onChange?.({ ...data, ...patch }),
    [data, onChange]
  );

  // Expose a validate method to parent
  useImperativeHandle(ref, () => ({
    validate: () => {
      // Validate both InstitutionComponent and ProgramComponent
      const institutionResult = institutionRef.current?.validate?.();
      const programResult = programRef.current?.validate?.();
      return institutionResult && programResult;
    },
  }));

  return (
    <div>
      {/* Institution section */}
      <div className={styles.institutionComponent} ref={institutionSectionRef}>
        <InstitutionSection
          ref={institutionRef}
          data={data.institution}
          onChange={(updated) => {
            applyUpdate({ institution: updated });
          }}
        />
      </div>
      {/* Program section */}
      <div className={styles.programComponent} ref={programSectionRef}>
        <ProgramSection
          ref={programRef}
          data={data.program}
          onChange={(updated) => {
            applyUpdate({ program: updated });
          }}
        />
      </div>
    </div>
  );
});

// -------------------- NewAcademicForm --------------------
export default function NewAcademicForm() {
  // ----------------- Container data & package state setup -----------------
  const { refetchServicePackages, academicRegisterPost } = CpnwContainer.useContainer();

  // Store package options from the backend
  const [programDisciplinePackages, setProgramDisciplinePackages] = useState([]);
  const [annualRatesPackages, setAnnualRatesPackages] = useState([]);
  const [checkrBackgroundPackages, setCheckrBackgroundPackages] = useState([]);
  const [watchReportPackages, setWatchReportPackages] = useState([]);
  const [multiYearPackages, setMultiYearPackages] = useState([]);
  const [paymentMethodPackages, setPaymentMethodPackages] = useState([]);
  const [checkrPaymentMethods, setCheckrPaymentMethods] = useState([]);
  const [watchPaymentMethods, setWatchPaymentMethods] = useState([]);

  const [postLoading, setPostLoading] = useState(false); // Tracks loading state during submission

  // ----------------- Default program form template -----------------
  const programFormData = {
    // Default structure of a program section
    programInfo: {
      programName: "",
      degreeAward: "",
      streetAddress: "",
      cityStateZip: "",
      primaryName: "",
      primaryEmail: "",
      backupName: "",
      backupEmail: ""
    },
    // All related package selections initialized from fetched data
    programDisciplinePackages: programDisciplinePackages,
    partnerType: PartnerTypes.CPNW_MEMBER,
    annualRatesPackages: annualRatesPackages,
    multiYearPackages: multiYearPackages,
    representativeInfo: {
      name: "",
      email: ""
    },
    paymentMethodPackages: paymentMethodPackages,
    checkrBackgroundPackages: checkrBackgroundPackages,
    watchReportPackages: watchReportPackages,
    checkrPaymentMethods: checkrPaymentMethods,
    watchPaymentMethods: watchPaymentMethods,
    invoicingInfo: {
      frequency: "Annually",
      invoicingEmail: "",
      copyEmail: "",
      billingStreet: "",
      billingCity: "",
      billingState: "",
      billingZip: "",
      additionalInfo: ""
    },
    placements: [],
    questions: "",
  };

  // ----------------- Form state -----------------
  const [formData, setFormData] = useState({
    institution: {
      name: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      primaryContactName: "",
      primaryContactEmail: "",
      primaryContactPhone: ""
    },
    program: {
      ...programFormData,
    },
    additionalPrograms: [], // List of additional program sections
  });

  // ----------------- Form references -----------------
  const primaryProgramRef = useRef(); // Ref to primary program section
  const programRefs = useRef([]); // Refs for additional programs

  // Sync refs when additionalPrograms changes
  const syncRefsWithPrograms = (programs) => {
    programRefs.current = programs.map((_, idx) => programRefs.current[idx] || React.createRef());
  };

  // Sync on mount and when programs added/removed
  useEffect(() => {
    syncRefsWithPrograms(formData.additionalPrograms);
  }, [formData.additionalPrograms]);


  /**
   * Extracts and transforms packages from a list by a specific type.
   * Also attempts to parse the description field if it's a JSON string.
   *
   * @param {Array} data - The array of package objects from the backend.
   * @param {string} type - The type of package to filter by.
   * @returns {Array} An array of package objects with consistent structure.
   */
  function getPackagesByType(data, type) {
    if (!Array.isArray(data)) return []; // Guard clause for invalid data

    return data
      .filter((pkg) => pkg.type === type) // Filter packages by desired type
      .map((pkg) => {
        let listItems = [];
        let description = "";

        try {
          // Attempt to parse the description as JSON (may include listItems)
          const parsed = JSON.parse(pkg.description);
          listItems = parsed.listItems || [];
        } catch (err) {
          // Fallback if description is plain text or invalid JSON
          listItems = [];
          description = pkg.description;
          // console.error(`Failed to parse description for package ID ${pkg.id}:`, err);
        }

        // Return a normalized package object
        return {
          id: pkg.id,
          name: pkg.name,
          description: description,
          isDefault: pkg.isDefault,
          isSelected: pkg.isSelected,
          options: pkg.options, // Option groups, if applicable
          listItems: listItems.map((item) => ({
            text: item.text,
            nestedItems: item.nestedItems || [], // Support for hierarchical display
          })),
        };
      });
  }

  // ----------------- Fetch and set service packages on mount -----------------
  useEffect(() => {
    refetchServicePackages().then((res) => {
      if (res?.data) {
        // Extract and store packages by type
        const programDisciplinePackages = getPackagesByType(res.data, "PROGRAM_DISCIPLINE");
        const servicePackages = getPackagesByType(res.data, "SERVICE_PACKAGE");
        const checkrBackgroundPackages = getPackagesByType(res.data, "CHECKR_BACKGROUND_SERVICE");
        const watchReportPackages = getPackagesByType(res.data, "WATCH_REPORT_SERVICE");
        const multiYearPackages = getPackagesByType(res.data, "MULTI_YEAR_PACKAGE");
        const paymentMethodPackages = getPackagesByType(res.data, "PAYMENT_METHOD");
        const checkrPaymentMethods = getPackagesByType(res.data, "CHECKR_PAYMENT_METHOD");
        const watchPaymentMethods = getPackagesByType(res.data, "WATCH_PAYMENT_METHOD");

        // Save in state
        setProgramDisciplinePackages(programDisciplinePackages);
        setAnnualRatesPackages(servicePackages);
        setCheckrBackgroundPackages(checkrBackgroundPackages);
        setWatchReportPackages(watchReportPackages);
        setMultiYearPackages(multiYearPackages);
        setPaymentMethodPackages(paymentMethodPackages);
        setCheckrPaymentMethods(checkrPaymentMethods);
        setWatchPaymentMethods(watchPaymentMethods);

        // Update formData with loaded packages
        setFormData((prev) => ({
          ...prev,
          program: {
            ...prev.program,
            programDisciplinePackages,
            annualRatesPackages: servicePackages,
            checkrBackgroundPackages,
            watchReportPackages,
            multiYearPackages,
            paymentMethodPackages,
            checkrPaymentMethods,
            watchPaymentMethods,
          },
        }));
      }
    });
  }, []);

  // ----------------- Validation logic -----------------
  const validate = async () => {
    // Validate main form section
    const isPrimaryValid = await Promise.resolve(primaryProgramRef.current?.validate?.()).then(res => res ?? true);

    // Validate all additional programs
    const areProgramsValid = await Promise.all(
      programRefs.current.map((ref) =>
        Promise.resolve(ref?.current?.validate?.()).then((res) => res ?? true)
      )
    );

    // Final validation result
    const allValid = isPrimaryValid && areProgramsValid.every(Boolean);
    return allValid;
  };

  // ----------------- Submission logic -----------------
  const handleSubmit = async () => {
    const isValid = await validate(); // Block submission if invalid
    if (!isValid) return;

    setPostLoading(true); // Show loading spinner
    console.log(formData);

    try {
      // Helper function to get selected packages
      const extractSelectedPackages = (packages) =>
        packages.flatMap(pkgArray =>
          pkgArray.filter(pkg => pkg.isSelected).map(({ id, name }) => ({ id, name }))
        );

      // Construct payload based on formData
      const academicPayload = {
        // Institution & primary program info
        parentEducationalName: formData.institution.name,
        educationalMailingAddress: formData.institution.address,
        city: formData.institution.city,
        state: formData.institution.state,
        zipCode: formData.institution.zip,
        primaryContactName: formData.institution.primaryContactName,
        primaryContactEmail: formData.institution.primaryContactEmail,
        primaryContactPhone: formData.institution.primaryContactPhone,
        educationalProgramName: formData.program.programInfo.programName,
        degreeAward: formData.program.programInfo.degreeAward,
        programStreetAddress: formData.program.programInfo.streetAddress,
        programLocation: formData.program.programInfo.cityStateZip,
        primaryCoordinatorName: formData.program.programInfo.primaryName,
        primaryCoordinatorEmail: formData.program.programInfo.primaryEmail,
        backupCoordinatorName: formData.program.programInfo.backupName,
        backupCoordinatorEmail: formData.program.programInfo.backupEmail,
        partnerType: formData.program.partnerType,
        representativeName: formData.program.representativeInfo.name,
        representativeEmail: formData.program.representativeInfo.email,
        invoiceFrequency: formData.program.invoicingInfo.frequency,
        invoiceEmail: formData.program.invoicingInfo.invoicingEmail,
        invoiceCcEmail: formData.program.invoicingInfo.copyEmail,
        billingStreet: formData.program.invoicingInfo.billingStreet,
        billingCity: formData.program.invoicingInfo.billingCity,
        billingState: formData.program.invoicingInfo.billingState,
        billingZip: formData.program.invoicingInfo.billingZip,
        billingInfo: formData.program.invoicingInfo.additionalInfo,
        placements: formData.program.placements,
        additionalInfo: formData.program.questions,

        // Handle all package selections including nested options
        servicePackages: [
          ...extractSelectedPackages([
            formData.program.programDisciplinePackages,
            formData.program.annualRatesPackages,
            formData.program.checkrBackgroundPackages,
            formData.program.watchReportPackages,
            formData.program.multiYearPackages,
            formData.program.paymentMethodPackages,
          ]),
          // Conditionally include payment method options
          ...(formData.program.checkrBackgroundPackages?.some(item => item.isSelected) &&
            formData.program.checkrPaymentMethods?.[0]?.options?.some(opt => opt.isSelected)
            ? [{ id: formData.program.checkrPaymentMethods[0].id, name: formData.program.checkrPaymentMethods[0].name, options: formData.program.checkrPaymentMethods[0].options.filter(opt => opt.isSelected) }]
            : []),
          ...(formData.program.watchReportPackages?.some(item => item.isSelected) &&
            formData.program.watchPaymentMethods?.[0]?.options?.some(opt => opt.isSelected)
            ? [{ id: formData.program.watchPaymentMethods[0].id, name: formData.program.watchPaymentMethods[0].name, options: formData.program.watchPaymentMethods[0].options.filter(opt => opt.isSelected) }]
            : []),
        ],

        // Repeat for additional programs
        additionalPrograms: formData.additionalPrograms.map((program) => ({
          educationalProgramName: program.programInfo.programName,
          degreeAward: program.programInfo.degreeAward,
          programStreetAddress: program.programInfo.streetAddress,
          programLocation: program.programInfo.cityStateZip,
          primaryCoordinatorName: program.programInfo.primaryName,
          primaryCoordinatorEmail: program.programInfo.primaryEmail,
          backupCoordinatorName: program.programInfo.backupName,
          backupCoordinatorEmail: program.programInfo.backupEmail,
          partnerType: program.partnerType,
          placements: program.placements,
          additionalInfo: program.questions,
          representativeName: program.representativeInfo.name,
          representativeEmail: program.representativeInfo.email,
          invoiceFrequency: program.invoicingInfo.frequency,
          invoiceEmail: program.invoicingInfo.invoicingEmail,
          invoiceCcEmail: program.invoicingInfo.copyEmail,
          billingStreet: program.invoicingInfo.billingStreet,
          billingCity: program.invoicingInfo.billingCity,
          billingState: program.invoicingInfo.billingState,
          billingZip: program.invoicingInfo.billingZip,
          billingInfo: program.invoicingInfo.additionalInfo,

          servicePackages: [
            ...extractSelectedPackages([
              program.programDisciplinePackages,
              program.annualRatesPackages,
              program.checkrBackgroundPackages,
              program.watchReportPackages,
              program.multiYearPackages,
              program.paymentMethodPackages,
            ]),
            ...(program.checkrBackgroundPackages?.some(item => item.isSelected) &&
              program.checkrPaymentMethods?.[0]?.options?.some(opt => opt.isSelected)
              ? [{ id: program.checkrPaymentMethods[0].id, name: program.checkrPaymentMethods[0].name, options: program.checkrPaymentMethods[0].options.filter(opt => opt.isSelected) }]
              : []),
            ...(program.watchReportPackages?.some(item => item.isSelected) &&
              program.watchPaymentMethods?.[0]?.options?.some(opt => opt.isSelected)
              ? [{ id: program.watchPaymentMethods[0].id, name: program.watchPaymentMethods[0].name, options: program.watchPaymentMethods[0].options.filter(opt => opt.isSelected) }]
              : []),
          ],
        })),
      };

      console.log('academicPayload', academicPayload)
      const result = await academicRegisterPost(academicPayload);

      if (result.success) {
        console.log("response", result);
        alert("Registration completed successfully!");
        window.location.reload(); // Optional reset
      } else {
        throw new Error("Submission failed");
      }

    } catch (error) {
      console.log(error);
      alert("Something went wrong while submitting the form. Please try again.");
    } finally {
      setPostLoading(false); // End loading state
    }
  };

  // ----------------- Event handlers -----------------

  // Update entire form object
  const handleChange = (updated) => {
    setFormData(updated);
  };

  // Update single program entry
  const handleAdditionalProgramChange = (index, updatedProgram) => {
    setFormData(prev => {
      const updatedPrograms = [...prev.additionalPrograms];
      updatedPrograms[index] = updatedProgram;
      return { ...prev, additionalPrograms: updatedPrograms };
    });
  };

  // Remove a program
  const handleRemoveAdditionalProgram = (index) => {
    setFormData(prev => {
      const updatedPrograms = [...prev.additionalPrograms];
      updatedPrograms.splice(index, 1);
      return { ...prev, additionalPrograms: updatedPrograms };
    });
  };

  // Add another program section
  const handleAddMore = () => {
    const newProgram = {
      ...structuredClone(programFormData),
      id: Date.now(), // Unique ID for rendering
    };

    setFormData(prev => ({
      ...prev,
      additionalPrograms: [...prev.additionalPrograms, newProgram],
    }));

    programRefs.current.push(React.createRef()); // Sync with new form
  };

  // ----------------- Render -----------------
  return (
    <div className={styles.HealthcareFormContainer}>
      <div className={styles.sectionContainer}>
        {/* Intro section */}
        <h1 className={styles.welcomeHeader}>Welcome Academic Partners</h1>
        <p>
          To complete this registration form, you will need to provide details
          about your academic program or programs you represent. Ensuring that
          all individual programs within your academic institution are listed
          will allow us to set up your CPNW account accurately. This ensures
          that student assignments to each program type are correctly managed
          and requirements are appropriately applied.
        </p>
      </div>

      {/* Primary program block */}
      <MainProgram
        ref={primaryProgramRef}
        data={formData}
        onChange={(updated) => handleChange(updated)}
      />

      {/* Render additional program components */}
      {formData.additionalPrograms.map((program, idx) => (
        <ProgramSection
          key={program.id}
          ref={programRefs.current[idx]}
          data={program}
          onChange={(updated) => handleAdditionalProgramChange(idx, updated)}
          onRemove={() => handleRemoveAdditionalProgram(idx)}
        />
      ))}

      {/* Button to add another program */}
      <div className={styles.addButtonContainer}>
        <button
          type="button"
          className={styles.addButton}
          onClick={(e) => { e.preventDefault(); handleAddMore(); }}
        >
          <FontAwesomeIcon icon={faPlus} className={styles.icon} /> Add
          Additional Program
        </button>
      </div>

      {/* Submit button */}
      <div className={styles.buttonContainer}>
        <SubmitButton onClick={handleSubmit} />
      </div>

      {/* Loading indicator on form submission */}
      {postLoading && (
        <div className={styles.overlay}>
          <div className={styles.loader}>
            <Loading />
          </div>
        </div>
      )}
    </div>
  );
}

// Define partner types used for categorizing institutions or programs
const PartnerTypes = {
  CPNW_MEMBER: "CPNW_MEMBER",
  COMMUNITY_PARTNER: "COMMUNITY_PARTNER",
};
